import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  AddonInfoResponse,
  AddonUploadView,
  ArtifactDetailResponse,
  ArtifactRedirectResponse,
  ArtifactType,
  BrowseResponse,
  CharacterRealm,
  CharactersListResponse,
  CharacterView,
  ClassIconsResponse,
  MeCharactersResponse,
  MeResponse,
  ProfessionIconsResponse,
  RecentItem,
  SearchResponse,
  StatsResponse,
  TooltipResponse,
} from '@thottbot/shared';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message?: string,
    public readonly body?: unknown,
  ) {
    super(message ?? code);
    this.name = 'ApiError';
  }
}

async function parse<T>(res: Response): Promise<T> {
  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const b = (body ?? {}) as { error?: string; message?: string };
    throw new ApiError(res.status, b.error ?? 'error', b.message, body);
  }
  return body as T;
}

export async function apiGet<T>(path: string): Promise<T> {
  return parse<T>(await fetch(path, { credentials: 'same-origin' }));
}

export async function apiSend<T>(method: string, path: string, body?: unknown): Promise<T> {
  return parse<T>(
    await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  );
}

export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  return parse<T>(await fetch(path, { method: 'POST', credentials: 'same-origin', body: form }));
}

export const queryKeys = {
  me: ['me'] as const,
  stats: ['stats'] as const,
  recent: ['recent'] as const,
  search: (q: string) => ['search', q] as const,
  browse: (type: string, params: string) => ['browse', type, params] as const,
  artifact: (type: string, slug: string) => ['artifact', type, slug] as const,
  tooltip: (type: string, slug: string) => ['tooltip', type, slug] as const,
  addonInfo: ['addon', 'info'] as const,
  addonUpload: (id: string) => ['addon', 'upload', id] as const,
  myCharacters: ['me', 'characters'] as const,
  character: (region: string, realm: string, name: string) => ['character', region, realm, name] as const,
  characters: (params: string) => ['characters', params] as const,
  characterRealms: ['characters', 'realms'] as const,
  buildOptions: ['builds', 'options'] as const,
  build: (id: string) => ['builds', id] as const,
  myBuilds: ['me', 'builds'] as const,
};

/** Addon version, minimum accepted version, download link and size cap (public). */
export function useAddonInfo(): UseQueryResult<AddonInfoResponse> {
  return useQuery({
    queryKey: queryKeys.addonInfo,
    queryFn: () => apiGet<AddonInfoResponse>('/api/addon/info'),
    staleTime: 60_000,
  });
}

/**
 * One upload's status, polled every 1.5 s while the server is still merging it. The poll
 * keeps running in a background tab: the app turns refetch-on-focus off, so a player who
 * switches tabs while waiting would otherwise never see the summary.
 */
export function useAddonUpload(id: string | null): UseQueryResult<AddonUploadView> {
  return useQuery({
    queryKey: queryKeys.addonUpload(id ?? ''),
    queryFn: () => apiGet<AddonUploadView>(`/api/addon/uploads/${id}`),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data?.status === 'processing' ? 1500 : false),
    refetchIntervalInBackground: true,
  });
}

export function useMe(): UseQueryResult<MeResponse> {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: () => apiGet<MeResponse>('/api/me'),
    staleTime: 60_000,
  });
}

export function useStats(): UseQueryResult<StatsResponse> {
  return useQuery({
    queryKey: queryKeys.stats,
    queryFn: () => apiGet<StatsResponse>('/api/stats'),
    staleTime: 60_000,
  });
}

export function useRecent(): UseQueryResult<{ items: RecentItem[] }> {
  return useQuery({
    queryKey: queryKeys.recent,
    queryFn: () => apiGet<{ items: RecentItem[] }>('/api/recent'),
    staleTime: 30_000,
  });
}

export function useSearch(q: string): UseQueryResult<SearchResponse> {
  return useQuery({
    queryKey: queryKeys.search(q),
    queryFn: () => apiGet<SearchResponse>(`/api/search?q=${encodeURIComponent(q)}`),
    enabled: q.trim().length >= 2,
  });
}

export function useBrowse(
  type: ArtifactType,
  params: URLSearchParams,
): UseQueryResult<BrowseResponse> {
  const qs = params.toString();
  return useQuery({
    queryKey: queryKeys.browse(type, qs),
    queryFn: () => apiGet<BrowseResponse>(`/api/browse/${type}${qs ? `?${qs}` : ''}`),
  });
}

/** Class name -> icon URL (client media); a missing file renders nothing, so consumers hide 404s. */
export function useClassIcons(): UseQueryResult<ClassIconsResponse> {
  return useQuery({
    queryKey: ['class-icons'] as const,
    queryFn: () => apiGet<ClassIconsResponse>('/api/class-icons'),
    staleTime: Infinity,
  });
}

/** Profession name -> icon URL (client media). */
export function useProfessionIcons(): UseQueryResult<ProfessionIconsResponse> {
  return useQuery({
    queryKey: ['profession-icons'] as const,
    queryFn: () => apiGet<ProfessionIconsResponse>('/api/profession-icons'),
    staleTime: Infinity,
  });
}

/**
 * The signed-in player's imported characters, polled every 1.5 s while any is still waiting
 * for its details (same background-tab reasoning as `useAddonUpload`).
 */
export function useMyCharacters(enabled: boolean): UseQueryResult<MeCharactersResponse> {
  return useQuery({
    queryKey: queryKeys.myCharacters,
    queryFn: () => apiGet<MeCharactersResponse>('/api/me/characters'),
    enabled,
    refetchInterval: (q) =>
      q.state.data?.importing || q.state.data?.characters.some((c) => c.detailStatus === 'pending') ? 1500 : false,
    refetchIntervalInBackground: true,
  });
}

export function useCharacter(region: string, realm: string, name: string): UseQueryResult<CharacterView, ApiError> {
  return useQuery({
    queryKey: queryKeys.character(region, realm, name),
    queryFn: () =>
      apiGet<CharacterView>(`/api/characters/${region}/${encodeURIComponent(realm)}/${encodeURIComponent(name)}`),
    retry: false,
  });
}

export function useCharacters(params: URLSearchParams): UseQueryResult<CharactersListResponse> {
  const qs = params.toString();
  return useQuery({
    queryKey: queryKeys.characters(qs),
    queryFn: () => apiGet<CharactersListResponse>(`/api/characters${qs ? `?${qs}` : ''}`),
  });
}

export function useCharacterRealms(): UseQueryResult<CharacterRealm[]> {
  return useQuery({
    queryKey: queryKeys.characterRealms,
    queryFn: () => apiGet<CharacterRealm[]>('/api/characters/realms'),
    staleTime: 60_000,
  });
}

export type ArtifactResult = ArtifactDetailResponse | ArtifactRedirectResponse;

export function isRedirect(r: ArtifactResult): r is ArtifactRedirectResponse {
  return 'redirectTo' in r;
}

/** An item's or ability's hover-tooltip fields; cached for the session so re-hovering is free. */
export function useTooltip(type: ArtifactType, slug: string): UseQueryResult<TooltipResponse, ApiError> {
  return useQuery({
    queryKey: queryKeys.tooltip(type, slug),
    queryFn: () => apiGet<TooltipResponse>(`/api/tooltip/${type}/${encodeURIComponent(slug)}`),
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export function useArtifact(
  type: ArtifactType,
  slug: string,
): UseQueryResult<ArtifactResult, ApiError> {
  return useQuery({
    queryKey: queryKeys.artifact(type, slug),
    queryFn: () => apiGet<ArtifactResult>(`/api/artifacts/${type}/${encodeURIComponent(slug)}`),
    retry: false,
  });
}
